import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FormControl, Validators } from '@angular/forms';
import { formatDate } from '@angular/common';
import { API_BASE_URL_SERVICE } from '../../../kiene-core.config';
import { KieneBackendApiService } from '../../../services/backend-api/kiene-backend-api.service';
import { HttpParams } from '@angular/common/http';
import { MessageService } from '../../../services/message-service/message.service';
import { GruppeStandortGewichtePreise, GruppeTierGewichtPreisDialogInput } from './gruppe-tier-gewicht-preis-dialog';
import { GruppeTierBulkOverwriteDialogComponent } from '../gruppe-tier-bulk-overwrite-dialog/gruppe-tier-bulk-overwrite-dialog.component';
import {
    BulkOverwriteAnimalRow,
    BulkOverwriteFieldSpec,
    BulkOverwriteMode,
    mapGruppeTierRecordToBulkRow,
    needsBulkOverwriteConfirm,
    resolveBulkPerTierValue,
} from '../gruppe-tier-bulk-overwrite-dialog/gruppe-tier-bulk-overwrite-dialog';
import { forkJoin, Observable, of } from 'rxjs';
import { Tierliste } from '../classes/tierliste';

@Component({
    selector: 'kiene-gruppe-tier-gewicht-preis-dialog',
    templateUrl: './gruppe-tier-gewicht-preis-dialog.component.html',
    styleUrls: ['./gruppe-tier-gewicht-preis-dialog.component.scss'],
})
export class GruppeTierGewichtPreisDialogComponent implements OnInit {
    isLoading = false;

    anzahlTiere: number;
    tiereMitDatum = 0;

    gesamtGewichtCtrl = new FormControl(undefined, [Validators.min(1)]);
    einzeltierGewichtCtrl = new FormControl(undefined, [Validators.min(1)]);
    gesamtpreisCtrl = new FormControl(undefined, [Validators.min(1)]);
    einzelpreisCtrl = new FormControl(undefined, [Validators.min(1)]);
    mwstCtrl = new FormControl(undefined, [Validators.required]);

    private animals: BulkOverwriteAnimalRow[] = [];

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public input: GruppeTierGewichtPreisDialogInput,
        public dialogRef: MatDialogRef<GruppeTierGewichtPreisDialogComponent>,
        @Inject(API_BASE_URL_SERVICE) private apiBaseUrl: string,
        private api: KieneBackendApiService,
        private messageService: MessageService,
        private dialog: MatDialog,
    ) {}

    get selectedTierlisten(): Tierliste[] {
        if (this.input?.tierlisten?.length) {
            return this.input.tierlisten;
        }
        return this.input?.tierliste ? [this.input.tierliste] : [];
    }

    ngOnInit(): void {
        this.ladeTiere();

        this.gesamtGewichtCtrl.valueChanges.subscribe({
            next: value => {
                if (value > 0 && this.anzahlTiere > 0) {
                    const v = Math.round((value / this.anzahlTiere) * 100) / 100.0;
                    this.einzeltierGewichtCtrl.setValue(v, { emitEvent: false });
                }
            },
        });

        this.einzeltierGewichtCtrl.valueChanges.subscribe({
            next: value => {
                if (value > 0 && this.anzahlTiere > 0) {
                    const v = Math.round(value * this.anzahlTiere * 100) / 100.0;
                    this.gesamtGewichtCtrl.setValue(v, { emitEvent: false });
                }
            },
        });

        this.gesamtpreisCtrl.valueChanges.subscribe({
            next: value => {
                if (value > 0 && this.anzahlTiere > 0) {
                    const v = Math.round((value / this.anzahlTiere) * 100) / 100.0;
                    this.einzelpreisCtrl.setValue(v, { emitEvent: false });
                }
            },
        });

        this.einzelpreisCtrl.valueChanges.subscribe({
            next: value => {
                if (value > 0 && this.anzahlTiere > 0) {
                    const v = Math.round(value * this.anzahlTiere * 100) / 100.0;
                    this.gesamtpreisCtrl.setValue(v, { emitEvent: false });
                }
            },
        });
    }

    save() {
        const fields = this.buildFieldsBeingSaved();
        if (!fields.length) {
            this.messageService.alertMessage('Bitte Gewicht oder Preis eingeben.');
            return;
        }

        const runPost = (mode: BulkOverwriteMode) => {
            if (this.input?.ausstallen === true) {
                const lists = this.selectedTierlisten.filter(l => Number(l?.tierliste_id) > 0);
                if (lists.length === 0) {
                    this.messageService.alertMessage('Keine Abgangsliste ausgewählt.');
                    return;
                }
                const gewichtField = fields.find(f => f.key === 'abgangsgewicht');
                const preisField = fields.find(f => f.key === 'kp_abgangspreis');
                const abgangsgewicht = gewichtField
                    ? resolveBulkPerTierValue(mode, this.animals, gewichtField)
                    : undefined;
                const kp_abgangspreis = preisField
                    ? resolveBulkPerTierValue(mode, this.animals, preisField)
                    : undefined;
                const url = this.apiBaseUrl + 'gruppe_tier/update_ausstalldaten.php';
                const requests: Observable<unknown>[] = lists.map(liste => {
                    const output = new GruppeStandortGewichtePreise();
                    output.overwrite_mode = mode;
                    output.gs_id = this.input?.gruppeStandort?.gs_id;
                    output.gruppe_id = this.input?.gruppeStandort?.gruppe_id;
                    output.tierliste_id = liste.tierliste_id;
                    if (abgangsgewicht != null) {
                        output.abgangsgewicht = abgangsgewicht;
                    }
                    if (kp_abgangspreis != null) {
                        output.kp_abgangspreis = kp_abgangspreis;
                    }
                    output.kp_abgangsmwstsatz = this.mwstCtrl.value;
                    return this.api.post(url, output);
                });
                forkJoin(requests.length ? requests : [of(null)]).subscribe({
                    next: () => {
                        this.messageService.infoMessage('Die Eingaben wurden erfolgreich gespeichert');
                        this.dialogRef.close(true);
                    },
                    error: err => {
                        this.messageService.errorMessage('Fehler!', err);
                    },
                });
                return;
            }

            if (this.input?.einstallen === true) {
                const output = new GruppeStandortGewichtePreise();
                output.overwrite_mode = mode;
                output.gs_id = this.input?.gruppeStandort?.gs_id;
                if (this.input?.viehhaendler?.viehhaendler_id) {
                    output.viehhaendler_id = this.input?.viehhaendler?.viehhaendler_id;
                    output.betriebskontakt_haendler_id = undefined;
                } else if (this.input?.betriebskontakt?.kunde_id) {
                    output.viehhaendler_id = undefined;
                    output.betriebskontakt_haendler_id = this.input?.betriebskontakt?.kunde_id;
                } else {
                    output.viehhaendler_id = undefined;
                    output.betriebskontakt_haendler_id = undefined;
                }
                output.einstalldatums = this.normalizeEinstalldatumsForApi(this.input?.einstalldatums);
                const gewichtField = fields.find(f => f.key === 'einstallgewicht');
                const preisField = fields.find(f => f.key === 'kp_einstallpreis');
                if (gewichtField) {
                    output.einstallgewicht = resolveBulkPerTierValue(mode, this.animals, gewichtField);
                }
                if (preisField) {
                    output.kp_einstallpreis = resolveBulkPerTierValue(mode, this.animals, preisField);
                }
                output.kp_einstallmwstsatz = this.mwstCtrl.value;

                this.api.post(this.apiBaseUrl + 'gruppe_tier/update_einstalldaten.php', output).subscribe({
                    next: () => {
                        this.messageService.infoMessage('Die Eingaben wurden erfolgreich gespeichert');
                        this.dialogRef.close(true);
                    },
                    error: err => {
                        this.messageService.errorMessage('Fehler!', err);
                    },
                });
            }
        };

        if (!needsBulkOverwriteConfirm(this.animals, fields)) {
            runPost('empty_only');
            return;
        }

        this.dialog
            .open(GruppeTierBulkOverwriteDialogComponent, {
                width: '640px',
                maxWidth: '95vw',
                disableClose: true,
                data: { fields, animals: this.animals },
            })
            .afterClosed()
            .subscribe(result => {
                if (result?.mode) {
                    runPost(result.mode);
                }
            });
    }

    private buildFieldsBeingSaved(): BulkOverwriteFieldSpec[] {
        const fields: BulkOverwriteFieldSpec[] = [];
        const gewichtGesamt = Number(this.gesamtGewichtCtrl.value);
        const gewichtPro = Number(this.einzeltierGewichtCtrl.value);
        const preisGesamt = Number(this.gesamtpreisCtrl.value);
        const preisPro = Number(this.einzelpreisCtrl.value);

        // Prefer Gesamt as source of truth when both are filled (linked controls).
        if (this.input?.ausstallen === true) {
            if (gewichtGesamt > 0 || gewichtPro > 0) {
                fields.push({
                    key: 'abgangsgewicht',
                    label: 'Abgangsgewicht',
                    gesamtValue: gewichtGesamt > 0 ? gewichtGesamt : null,
                    proTierValue: gewichtPro > 0 ? gewichtPro : null,
                    preferProTier: !(gewichtGesamt > 0) && gewichtPro > 0,
                    unit: 'kg',
                });
            }
            if (preisGesamt > 0 || preisPro > 0) {
                fields.push({
                    key: 'kp_abgangspreis',
                    label: 'Verkaufspreis',
                    gesamtValue: preisGesamt > 0 ? preisGesamt : null,
                    proTierValue: preisPro > 0 ? preisPro : null,
                    preferProTier: !(preisGesamt > 0) && preisPro > 0,
                    unit: '€',
                });
            }
        } else if (this.input?.einstallen === true) {
            if (gewichtGesamt > 0 || gewichtPro > 0) {
                fields.push({
                    key: 'einstallgewicht',
                    label: 'Einstallgewicht',
                    gesamtValue: gewichtGesamt > 0 ? gewichtGesamt : null,
                    proTierValue: gewichtPro > 0 ? gewichtPro : null,
                    preferProTier: !(gewichtGesamt > 0) && gewichtPro > 0,
                    unit: 'kg',
                });
            }
            if (preisGesamt > 0 || preisPro > 0) {
                fields.push({
                    key: 'kp_einstallpreis',
                    label: 'Einkaufspreis',
                    gesamtValue: preisGesamt > 0 ? preisGesamt : null,
                    proTierValue: preisPro > 0 ? preisPro : null,
                    preferProTier: !(preisGesamt > 0) && preisPro > 0,
                    unit: '€',
                });
            }
        }
        return fields;
    }

    private zaehleTiereMitAbgangOderTod(records: Record<string, unknown>[]): number {
        const keys = [
            'abgangsdatum_notnull',
            'hit_abgangsdatum',
            'kp_abgangsdatum',
            'todesdatum_notnull',
            'hit_todesdatum',
            'kp_todesdatum',
        ];
        return records.filter(rec => this.hatDatum(rec, keys)).length;
    }

    private hatDatum(rec: Record<string, unknown>, keys: string[]): boolean {
        return keys.some(key => {
            const value = rec[key];
            return value != null && String(value).trim() !== '';
        });
    }

    private normalizeEinstalldatumsForApi(dates?: Array<Date | string>): string[] {
        if (!dates?.length) {
            return [];
        }
        return dates.map(d => {
            if (typeof d === 'string') {
                return d.length >= 10 ? d.slice(0, 10) : d;
            }
            return formatDate(d, 'yyyy-MM-dd', 'de', 'Europe/Berlin');
        });
    }

    private ladeTiere() {
        let params = undefined;
        if (this.input?.einstallen === true) {
            params = new HttpParams().set('gruppe_id', this.input?.gruppeStandort?.gruppe_id?.toString());
            if (this.input?.viehhaendler?.viehhaendler_id) {
                params = params.set('viehhaendler_id[]', this.input?.viehhaendler?.viehhaendler_id?.toString());
            }
            if (this.input?.betriebskontakt?.kunde_id) {
                params = params.set(
                    'betriebskontakt_haendler_id[]',
                    this.input?.betriebskontakt?.kunde_id?.toString(),
                );
            }
            for (const ed of this.input?.einstalldatums) {
                params = params.append('einstalldatum[]', formatDate(ed, 'yyyy-MM-dd', 'de', 'Europe/Berlin'));
            }
        } else if (this.input?.ausstallen === true) {
            const listIds = this.selectedTierlisten
                .map(liste => Number(liste?.tierliste_id))
                .filter(id => id > 0);
            if (listIds.length > 0) {
                params = new HttpParams().set('gruppe_id', this.input?.gruppeStandort?.gruppe_id?.toString());
                for (const id of listIds) {
                    params = params.append('tierliste_id[]', String(id));
                }
            }
        }
        if (params != undefined) {
            this.isLoading = true;
            this.api.getAll(this.apiBaseUrl + 'gruppe_tier/read_kundenportal.php', params).subscribe({
                next: response => {
                    const records = (response?.records ?? []) as Record<string, unknown>[];
                    this.animals = records.map(mapGruppeTierRecordToBulkRow);
                    this.anzahlTiere = this.animals.length;
                    this.tiereMitDatum = this.zaehleTiereMitAbgangOderTod(records);
                    this.isLoading = false;
                },
                error: err => {
                    this.messageService.errorMessage('Fehler', err);
                    this.isLoading = false;
                },
            });
        }
    }
}
